import { SpatialAggregationType } from "@/types/common";
import {
  DataSchemaType,
  Dataset,
  DatasetAttributeSchema,
  DatasetData,
  DatasetDataColumn,
  DatasetDataType,
  DatasetDetailResponse,
  DatasetProperties,
  DatasetStyle,
  EColorLabel,
  EColorNames,
  LabelConfig,
  RankConfig,
  StrokeConfig
} from "@/types/dataset";
import { LayerType } from "@/types/map";

/**
 * Deep partial type that makes all properties, including nested ones, optional.
 */
export type DeepPartial<T> = T extends object
  ? { [P in keyof T]?: DeepPartial<T[P]> }
  : T;

/**
 * Deep merges two objects, with source values overriding target values.
 * Arrays are replaced, not merged.
 */
function deepMerge<T extends object>(target: T, source: DeepPartial<T>): T {
  const result = { ...target };

  (Object.keys(source) as Array<keyof T>).forEach((key) => {
    const sourceValue = source[key as keyof DeepPartial<T>];
    const targetValue = target[key];

    if (
      sourceValue !== undefined &&
      typeof sourceValue === "object" &&
      sourceValue !== null &&
      !Array.isArray(sourceValue) &&
      typeof targetValue === "object" &&
      targetValue !== null &&
      !Array.isArray(targetValue)
    ) {
      (result as Record<string, unknown>)[key as string] = deepMerge(
        targetValue as object,
        sourceValue as DeepPartial<object>
      );
    } else if (sourceValue !== undefined) {
      (result as Record<string, unknown>)[key as string] = sourceValue;
    }
  });

  return result;
}

/**
 * Creates a mock DatasetAttributeSchema with sensible defaults.
 * Override any properties by passing a partial object.
 *
 * @example
 * const schema = createMockDatasetAttributeSchema();
 * const customSchema = createMockDatasetAttributeSchema({
 *   id: "custom-id",
 *   attribute_name: "custom_attribute"
 * });
 */
export function createMockDatasetAttributeSchema(
  overrides: DeepPartial<DatasetAttributeSchema> = {}
): DatasetAttributeSchema {
  const defaults: DatasetAttributeSchema = {
    id: "mock-schema-id",
    code: "mock_code",
    attribute_name: "mock_attribute",
    attribute_code: "mock_attribute_code",
    data_type: DataSchemaType.TEXT,
    data_label: undefined,
    is_nullable: true,
    is_reference_geo: false,
    is_reference_id: false,
    is_spatial: false,
    position: 0,
    created_at: "2024-01-01T00:00:00.000Z",
    updated_at: "2024-01-01T00:00:00.000Z",
    time_format: null,
    is_visible: true,
    paired_key_attribute_id: null
  };

  return deepMerge(defaults, overrides);
}

/**
 * Creates a mock LabelConfig with sensible defaults.
 */
export function createMockLabelConfig(
  overrides: DeepPartial<LabelConfig> = {}
): LabelConfig {
  const defaults: LabelConfig = {
    code: "mock_code",
    attribute_id: "mock-attribute-id",
    attribute_name: "mock_attribute",
    show_label: true,
    color_scale: EColorLabel.BLACK_WHITE,
    opacity: 1
  };

  return deepMerge(defaults, overrides);
}

/**
 * Creates a mock StrokeConfig with sensible defaults.
 */
export function createMockStrokeConfig(
  overrides: DeepPartial<StrokeConfig> = {}
): StrokeConfig {
  const defaults: StrokeConfig = {
    code: "mock_code",
    attribute_id: "mock-stroke-id",
    attribute_name: "mock_stroke_attribute",
    width: 1,
    opacity: 1
  };

  return deepMerge(defaults, overrides);
}

/**
 * Creates a mock RankConfig with sensible defaults.
 */
export function createMockRankConfig(
  overrides: DeepPartial<RankConfig> = {}
): RankConfig {
  const defaults: RankConfig = {
    isEnabled: false,
    maxRanking: 10
  };

  return deepMerge(defaults, overrides);
}

/**
 * Creates a mock DatasetStyle with sensible defaults.
 * Override any properties by passing a partial object.
 *
 * @example
 * const style = createMockDatasetStyle();
 * const customStyle = createMockDatasetStyle({
 *   fillColor: "#FF0000",
 *   colorFamily: EColorNames.RED
 * });
 */
export function createMockDatasetStyle(
  overrides: DeepPartial<DatasetStyle> = {}
): DatasetStyle {
  const defaults: DatasetStyle = {
    colorRanges: [],
    color: "#FFFFFF",
    weight: 1,
    opacity: 1,
    fillColor: "#00B5D8",
    fillOpacity: 0.7,
    visibility: "visible",
    colorFamily: EColorNames.BLUE,
    labelConfig: createMockLabelConfig(),
    colorSteps: 10,
    rankConfig: createMockRankConfig(),
    stroke: createMockStrokeConfig()
  };

  return deepMerge(defaults, overrides);
}

/**
 * Creates a mock DatasetProperties with sensible defaults.
 */
export function createMockDatasetProperties(
  overrides: DeepPartial<DatasetProperties> = {}
): DatasetProperties {
  const defaults: DatasetProperties = {
    isVisible: true,
    spatialType: LayerType.POLYGON,
    isFetching: false
  };

  return deepMerge(defaults, overrides);
}

/**
 * Creates a mock DatasetDetailResponse (the `data` portion of a Dataset).
 * Override any properties by passing a partial object.
 *
 * @example
 * const data = createMockDatasetDetailResponse();
 * const customData = createMockDatasetDetailResponse({
 *   id: "custom-id",
 *   name: "Custom Dataset",
 *   available: false
 * });
 */
export function createMockDatasetDetailResponse(
  overrides: DeepPartial<DatasetDetailResponse> = {}
): DatasetDetailResponse {
  const defaults: DatasetDetailResponse = {
    id: "mock-dataset-id",
    name: "Mock Dataset",
    display_name: "Mock Dataset",
    bbox: undefined,
    country_id: "id",
    source: "bvt",
    spatial_aggregation_type: "polygon",
    short_description: "Mock dataset description",
    published: true,
    created_at: "2024-01-01T00:00:00.000Z",
    updated_at: "2024-01-01T00:00:00.000Z",
    description: undefined,
    available: true,
    total_rows: 100,
    adm_area_level: null,
    grid_level: null,
    license: undefined,
    is_subscribed: undefined,
    provider: undefined,
    min_area_level_filter: undefined,
    dataset_size_kb: undefined,
    size_kb: undefined,
    schema: []
  };

  return deepMerge(defaults, overrides);
}

/**
 * Creates a mock Dataset with sensible defaults.
 * Override any properties by passing a partial object.
 * Supports deep merging for nested properties.
 *
 * @example
 * // Use all defaults
 * const dataset = createMockDataset();
 *
 * // Override specific nested properties
 * const customDataset = createMockDataset({
 *   data: { name: "Custom Name", available: false },
 *   properties: { isVisible: false },
 *   style: { fillColor: "#FF0000" }
 * });
 *
 * // Override with specific spatial type
 * const pointDataset = createMockDataset({
 *   data: { spatial_aggregation_type: "poi" },
 *   properties: { spatialType: "poi" }
 * });
 */
export function createMockDataset(
  overrides: DeepPartial<Dataset> = {}
): Dataset {
  const defaultSelectedCategory = createMockDatasetAttributeSchema({
    id: "selected-category-id",
    attribute_name: "selected_category"
  });

  const defaults: Dataset = {
    data: createMockDatasetDetailResponse(),
    properties: createMockDatasetProperties(),
    style: createMockDatasetStyle(),
    categories: [],
    selectedCategory: defaultSelectedCategory,
    h3Configuration: null
  };

  return deepMerge(defaults, overrides);
}

/**
 * Creates a mock DatasetDataColumn with sensible defaults.
 */
export function createMockDatasetDataColumn(
  overrides: DeepPartial<DatasetDataColumn> = {}
): DatasetDataColumn {
  const defaults: DatasetDataColumn = {
    schema_id: "mock-schema-id",
    code: "mock_column_code",
    name: "Mock Column",
    data_type: DatasetDataType.TYPE_STRING,
    is_visible: true,
    is_reference_geo: false
  };

  return deepMerge(defaults, overrides);
}

/**
 * Creates a mock DatasetData (raw dataset) with sensible defaults.
 * Override any properties by passing a partial object.
 *
 * @example
 * const rawDataset = createMockDatasetData();
 * const customRawDataset = createMockDatasetData({
 *   dataset_id: "custom-id",
 *   name: "Custom Dataset",
 *   rows: [["value1", "value2"]]
 * });
 */
export function createMockDatasetData(
  overrides: DeepPartial<DatasetData> = {}
): DatasetData {
  const defaults: DatasetData = {
    columns: [],
    rows: [],
    dataset_id: "mock-dataset-id",
    dataset_size_kb: 100,
    name: "Mock Dataset",
    display_name: "Mock Dataset",
    spatial_aggregation_type: "polygon" as SpatialAggregationType
  };

  return deepMerge(defaults, overrides);
}

/**
 * Creates a mock Dataset with a specific ID for use in tests that need multiple datasets.
 * Convenience wrapper around createMockDataset.
 *
 * @example
 * const dataset1 = createMockDatasetWithId("dataset-1");
 * const dataset2 = createMockDatasetWithId("dataset-2", {
 *   data: { name: "Second Dataset" }
 * });
 */
export function createMockDatasetWithId(
  id: string,
  overrides: DeepPartial<Dataset> = {}
): Dataset {
  return createMockDataset(
    deepMerge(
      {
        data: { id, name: `Dataset ${id}` }
      },
      overrides
    )
  );
}

/**
 * Creates a mock Dataset configured for a specific spatial type.
 * Convenience wrapper around createMockDataset.
 *
 * @example
 * const pointDataset = createMockDatasetWithSpatialType("poi");
 * const polygonDataset = createMockDatasetWithSpatialType("polygon");
 * const gridDataset = createMockDatasetWithSpatialType("grid");
 */
export function createMockDatasetWithSpatialType(
  spatialType: SpatialAggregationType,
  overrides: DeepPartial<Dataset> = {}
): Dataset {
  return createMockDataset(
    deepMerge(
      {
        data: { spatial_aggregation_type: spatialType },
        properties: { spatialType }
      },
      overrides
    )
  );
}
